import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Clock, AlertTriangle, Shield, Camera, Mic, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import LaTeXRenderer from '../../components/LaTeXRenderer';
import { SystemCheck, IdentityVerification, ProctoringOverlay } from '../../components/Proctoring';
import { stopSharedStream } from '../../lib/proctorStream';

// Proctoring phases
const PHASE = {
  LOADING: 'loading',
  SYSTEM_CHECK: 'system_check',
  IDENTITY_VERIFICATION: 'identity_verification',
  TAKING_TEST: 'taking_test',
  FULLSCREEN_PROMPT: 'fullscreen_prompt',
  INSTRUCTIONS: 'instructions',
  COMPLETED: 'completed'
};

function TakeTest() {
  const { testId } = useParams();
  const navigate = useNavigate();
  
  // Test state
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attempt, setAttempt] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [violations, setViolations] = useState(0);
  const [showTerminationDialog, setShowTerminationDialog] = useState(false);
  const [terminationReason, setTerminationReason] = useState('');
  const timerRef = useRef(null);
  
  // Advanced proctoring state
  const [phase, setPhase] = useState(PHASE.LOADING);
  const [proctoringSession, setProctoringSession] = useState(null);
  const [proctoringSettings, setProctoringSettings] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [trustScore, setTrustScore] = useState(100);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  
  // Ready state - only true when everything is loaded
  const [isReady, setIsReady] = useState(false);
  
  // Use ref to store test data to avoid closure issues
  const testRef = useRef(null);
  
  // Keep testRef in sync with test state
  useEffect(() => {
    testRef.current = test;
  }, [test]);

  // Security lockdown: block right-click, DevTools, copy, screenshots during test
  useEffect(() => {
    if (phase !== PHASE.TAKING_TEST) return;

    // Block right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      toast.warning('Right-click is disabled during the exam');
      return false;
    };

    // Block keyboard shortcuts for DevTools, copy, paste, screenshot
    const handleKeyDown = (e) => {
      // F12 - DevTools
      if (e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
        toast.warning('Developer tools are disabled during the exam');
        return false;
      }
      // Ctrl+Shift+I or Ctrl+Shift+J - DevTools
      if (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j')) {
        e.preventDefault();
        e.stopPropagation();
        toast.warning('Developer tools are disabled during the exam');
        return false;
      }
      // Ctrl+U - View Source
      if (e.ctrlKey && (e.key === 'U' || e.key === 'u')) {
        e.preventDefault();
        toast.warning('View source is disabled during the exam');
        return false;
      }
      // Ctrl+C - Copy
      if (e.ctrlKey && (e.key === 'C' || e.key === 'c')) {
        e.preventDefault();
        toast.warning('Copying is disabled during the exam');
        return false;
      }
      // Ctrl+V - Paste
      if (e.ctrlKey && (e.key === 'V' || e.key === 'v')) {
        e.preventDefault();
        toast.warning('Pasting is disabled during the exam');
        return false;
      }
      // Ctrl+A - Select All
      if (e.ctrlKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault();
        return false;
      }
      // Ctrl+S - Save
      if (e.ctrlKey && (e.key === 'S' || e.key === 's')) {
        e.preventDefault();
        return false;
      }
      // Ctrl+P - Print
      if (e.ctrlKey && (e.key === 'P' || e.key === 'p')) {
        e.preventDefault();
        toast.warning('Printing is disabled during the exam');
        return false;
      }
      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        toast.warning('Screenshots are disabled during the exam');
        return false;
      }
    };

    // Block copy and paste events
    const handleCopy = (e) => {
      e.preventDefault();
      toast.warning('Copying is disabled during the exam');
    };
    const handlePaste = (e) => {
      e.preventDefault();
      toast.warning('Pasting is disabled during the exam');
    };
    const handleCut = (e) => {
      e.preventDefault();
    };

    // Block drag
    const handleDragStart = (e) => {
      e.preventDefault();
    };

    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('copy', handleCopy, true);
    document.addEventListener('paste', handlePaste, true);
    document.addEventListener('cut', handleCut, true);
    document.addEventListener('dragstart', handleDragStart, true);

    // Add CSS to disable text selection
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('copy', handleCopy, true);
      document.removeEventListener('paste', handlePaste, true);
      document.removeEventListener('cut', handleCut, true);
      document.removeEventListener('dragstart', handleDragStart, true);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, [phase]);
  useEffect(() => {
    initializeTest();
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [testId]);

  const initializeTest = async () => {
    try {
      setPhase(PHASE.LOADING);
      
      // Fetch test info first to check proctoring requirements
      const testRes = await api.get(`/tests/${testId}`);
      const testData = testRes.data;
      setTest(testData);
      testRef.current = testData; // Also set ref immediately
      
      const proctoringLevel = testData.proctoring_level || 'none';
      
      if (proctoringLevel !== 'none') {
        // Fetch proctoring settings for this level
        const levelsRes = await api.get('/proctoring/levels');
        const settings = levelsRes.data[proctoringLevel];
        setProctoringSettings(settings);
        
        // Start with system check if proctoring is enabled
        setPhase(PHASE.SYSTEM_CHECK);
        
        // Pre-create attempt + session so continuous recording can start at
        // camera grant (mobile QR also needs the session ID upfront).
        if (settings.mobile_proctoring_enabled || settings.continuous_video_recording) {
          try {
            const attemptRes = await api.post('/attempts/start', { test_id: testId });
            const attemptData = attemptRes.data;
            setAttempt(attemptData);
            const sessionRes = await api.post(`/proctoring/session/start?attempt_id=${attemptData.id}`);
            setProctoringSession(sessionRes.data);
          } catch (err) {
            console.error('Error pre-creating session:', err);
            toast.error('Failed to create proctoring session. Please retry.');
          }
        }
      } else {
        // No proctoring - go directly to loading test
        await loadAndStartTest(testData);
      }
    } catch (error) {
      console.error('Error initializing test:', error);
      toast.error('Failed to load test. Please try again.');
      navigate('/dashboard');
    }
  };

  const handleSystemCheckComplete = async (stream) => {
    setCameraStream(stream);
    
    const currentTest = testRef.current;
    
    if (!currentTest) {
      console.error('Test data not available');
      toast.error('Test data not loaded. Please refresh and try again.');
      navigate('/dashboard');
      return;
    }
    
    if (proctoringSettings?.identity_verification_required) {
      // If session was already created for mobile proctoring, skip creation
      if (proctoringSession?.session_id) {
        setPhase(PHASE.IDENTITY_VERIFICATION);
        return;
      }
      // Identity verification needs a proctoring session (which needs an attempt).
      try {
        setPhase(PHASE.LOADING);
        
        // Create attempt
        const attemptRes = await api.post('/attempts/start', { test_id: testId });
        const attemptData = attemptRes.data;
        setAttempt(attemptData);
        
        // Create proctoring session
        const sessionRes = await api.post(`/proctoring/session/start?attempt_id=${attemptData.id}`);
        setProctoringSession(sessionRes.data);
        
        // Now we can safely enter identity verification
        setPhase(PHASE.IDENTITY_VERIFICATION);
      } catch (error) {
        console.error('Error setting up proctoring session:', error);
        let errorMessage = 'Failed to start test. Please try again.';
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage);
        navigate('/dashboard');
      }
    } else {
      setPhase(PHASE.LOADING);
      await loadAndStartTest(currentTest);
    }
  };

  const handleIdentityVerified = async () => {
    const currentTest = testRef.current;
    
    if (!currentTest) {
      toast.error('Test data not loaded. Please refresh and try again.');
      navigate('/dashboard');
      return;
    }
    
    // Attempt and proctoring session already created in handleSystemCheckComplete.
    // Just load questions and start the timer.
    setPhase(PHASE.LOADING);
    await loadQuestionsAndStart(currentTest);
  };

  // Loads questions, starts timer, and transitions to TAKING_TEST phase.
  // Assumes attempt (and proctoring session if needed) already exist in state.
  const loadQuestionsAndStart = async (testData) => {
    try {
      if (!testData || !testData.id) {
        throw new Error('Invalid test data');
      }
      
      // Fetch all questions
      console.log('Fetching questions for test:', testId);
      const questionsRes = await api.get(`/tests/${testId}/questions`);
      const questionsData = questionsRes.data;
      console.log('Questions fetched:', questionsData?.length);
      
      if (!questionsData || questionsData.length === 0) {
        throw new Error('No questions found for this test');
      }
      
      // Map questions by ID for ordering
      const questionMap = {};
      questionsData.forEach(q => {
        questionMap[q.id] = q;
      });
      
      // Order questions as per test configuration
      const orderedQuestions = testData.question_ids
        .map(qId => questionMap[qId])
        .filter(q => q);
      
      if (orderedQuestions.length === 0) {
        throw new Error('Could not load test questions');
      }
      
      // Update state with questions
      setQuestions(orderedQuestions);
      
      // Start timer
      const duration = testData.config.duration * 60;
      setTimeRemaining(duration);
      
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Mark as ready
      setIsReady(true);
      
      // Always show instructions first, then fullscreen if needed
      setPhase(PHASE.INSTRUCTIONS);
      
    } catch (error) {
      console.error('Error loading questions:', error);
      let errorMessage = 'Failed to load test questions. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      navigate('/dashboard');
    }
  };

  // Full flow: creates attempt, optionally creates proctoring session, then loads questions.
  // Used for non-proctored tests and proctored tests WITHOUT identity verification.
  const loadAndStartTest = async (testData) => {
    try {
      if (!testData || !testData.id) {
        throw new Error('Invalid test data');
      }
      
      // Start the test attempt
      console.log('Starting attempt for test:', testId);
      const attemptRes = await api.post('/attempts/start', { test_id: testId });
      const attemptData = attemptRes.data;
      console.log('Attempt created:', attemptData.id);
      setAttempt(attemptData);
      
      // Start proctoring session if proctoring is enabled
      if (testData.proctoring_level && testData.proctoring_level !== 'none') {
        try {
          console.log('Starting proctoring session...');
          const sessionRes = await api.post(`/proctoring/session/start?attempt_id=${attemptData.id}`);
          setProctoringSession(sessionRes.data);
          console.log('Proctoring session started');
        } catch (err) {
          console.error('Failed to start proctoring session:', err);
          toast.warning('Proctoring session could not be started, but test will continue.');
        }
      }
      
      // Now load questions and start
      await loadQuestionsAndStart(testData);
      
    } catch (error) {
      console.error('Error starting test:', error);
      let errorMessage = 'Failed to start test. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      navigate('/dashboard');
    }
  };

  // Legacy proctoring event handlers (kept for backward compatibility)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && test?.config?.proctoring_enabled) {
        logProctoringEvent('tab_switch');
      }
    };

    const handleContextMenu = (e) => {
      if (test?.config?.proctoring_enabled) {
        e.preventDefault();
        logProctoringEvent('context_menu');
      }
    };

    const handleCopy = (e) => {
      if (test?.config?.proctoring_enabled) {
        e.preventDefault();
        logProctoringEvent('copy_attempt');
      }
    };

    const handlePaste = (e) => {
      if (test?.config?.proctoring_enabled) {
        e.preventDefault();
        logProctoringEvent('paste_attempt');
      }
    };

    if (test?.config?.proctoring_enabled && phase === PHASE.TAKING_TEST) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('copy', handleCopy);
      document.addEventListener('paste', handlePaste);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [test, phase]);

  const enterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch(err => console.log('Fullscreen error:', err));
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  // Listen for fullscreen exit during test — show modal to force re-entry
  useEffect(() => {
    if (phase !== PHASE.TAKING_TEST) return;
    
    const handleFullscreenChange = () => {
      const isFs = !!document.fullscreenElement || !!document.webkitFullscreenElement;
      if (!isFs) {
        setShowFullscreenModal(true);
      } else {
        setShowFullscreenModal(false);
      }
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [phase]);

  const handleReEnterFullscreen = () => {
    enterFullscreen();
    setShowFullscreenModal(false);
  };

  // Called from button click — user gesture context, fullscreen will work
  const handleEnterFullscreenAndStart = () => {
    enterFullscreen();
    setPhase(PHASE.TAKING_TEST);
  };

  // Called from instructions page — proceed to fullscreen or test
  const handleProceedFromInstructions = () => {
    const requiresFullscreen = test?.config?.require_fullscreen || 
                               (test?.proctoring_level && test.proctoring_level !== 'none');
    if (requiresFullscreen) {
      setPhase(PHASE.FULLSCREEN_PROMPT);
    } else {
      setPhase(PHASE.TAKING_TEST);
    }
  };

  const logProctoringEvent = async (eventType) => {
    try {
      const response = await api.post('/proctoring/event', {
        attempt_id: attempt.id,
        event_type: eventType,
        meta: {},
      });
      
      if (response.data.terminated) {
        // Auto-terminate: Submit partial answers
        const reason = `Tab switch limit exceeded (${test.config.tab_switch_limit} violations)`;
        setTerminationReason(reason);
        await handleTermination(reason);
      } else {
        const newViolations = violations + 1;
        setViolations(newViolations);
        toast.warning(`⚠️ Violation ${newViolations}/${test.config.tab_switch_limit} - Test will be terminated if limit is reached!`);
      }
    } catch (error) {
      console.error('Error logging proctoring event:', error);
    }
  };

  const handleTermination = async (reason) => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    // Prepare partial answers
    const answersArray = questions.map(q => ({
      question_id: q.id,
      answer: answers[q.id] || '',
      time_spent: 0,
    }));

    try {
      // Submit with termination reason
      await api.post('/attempts/submit', {
        attempt_id: attempt.id,
        answers: answersArray,
        termination_reason: reason,
      });
      
      setShowTerminationDialog(true);
    } catch (error) {
      console.error('Error submitting terminated test:', error);
      toast.error('Failed to submit test');
    }
  };

  const handleAnswerChange = (answer) => {
    const question = questions[currentQuestion];
    console.log('Answer changed:', { questionId: question.id, answer, currentQuestion });
    setAnswers(prev => {
      const newAnswers = {
        ...prev,
        [question.id]: answer,
      };
      console.log('Updated answers state:', newAnswers);
      return newAnswers;
    });
  };

  const handleSubmit = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    console.log('Submitting answers:', answers);
    console.log('Total answers captured:', Object.keys(answers).length);
    
    const answersArray = questions.map(q => ({
      question_id: q.id,
      answer: answers[q.id] !== undefined ? answers[q.id] : '',
      time_spent: 0,
    }));
    
    console.log('Answers array being sent:', answersArray);

    try {
      const response = await api.post('/attempts/submit', {
        attempt_id: attempt.id,
        answers: answersArray,
      });
      
      // End proctoring session if active
      if (proctoringSession?.session_id) {
        try {
          await api.post(`/proctoring/session/end?session_id=${proctoringSession.session_id}`);
        } catch (err) {
          console.error('Failed to end proctoring session:', err);
        }
      }
      
      // Stop camera stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      
      toast.success('Test submitted successfully');
      
      // Navigate to results if allowed
      if (test.config.show_results_immediately) {
        navigate(`/results/${attempt.id}`);
      } else {
        toast.info('Results will be available once reviewed by admin');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      const detail = error.response?.data?.detail || 'Unknown error';
      toast.error(`Failed to submit test: ${detail}`);
    }
  };

  const handleProctoringTerminate = async (reason) => {
    setTerminationReason(reason);
    await handleTermination(reason);
  };

  const getToken = () => {
    return localStorage.getItem('token');
  };

  // RENDER LOGIC - based ONLY on phase state
  
  // Loading phase - show spinner
  if (phase === PHASE.LOADING) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Loading Test...</h2>
          <p className="text-gray-600">Please wait while we prepare your test</p>
        </div>
      </div>
    );
  }

  // System Check Phase
  if (phase === PHASE.SYSTEM_CHECK && proctoringSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
        <SystemCheck
          settings={proctoringSettings}
          onComplete={handleSystemCheckComplete}
          onCancel={() => { stopSharedStream(); navigate('/dashboard'); }}
          sessionId={proctoringSession?.session_id}
        />
      </div>
    );
  }

  // Identity Verification Phase
  if (phase === PHASE.IDENTITY_VERIFICATION) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 p-4">
        <IdentityVerification
          sessionId={proctoringSession?.session_id}
          token={getToken()}
          onComplete={handleIdentityVerified}
          onCancel={() => { stopSharedStream(); navigate('/dashboard'); }}
        />
      </div>
    );
  }

  // Instructions Phase — show test rules tailored to the selected proctoring level
  if (phase === PHASE.INSTRUCTIONS) {
    const ps = proctoringSettings || {};
    const isProctored = !!(test?.proctoring_level && test.proctoring_level !== 'none');

    // Build the "Strictly Prohibited" list based on what is actually enforced
    const prohibited = [];
    if (ps.max_tab_switches > 0 || ps.fullscreen_required) prohibited.push('Do NOT switch tabs or open other applications');
    if (ps.fullscreen_required) prohibited.push('Do NOT exit fullscreen mode during the test');
    prohibited.push('Do NOT use mobile phones or any electronic devices');
    if (ps.audio_monitoring_enabled) prohibited.push('Do NOT talk or communicate with anyone');
    prohibited.push('Do NOT copy, paste, or right-click during the test');
    if (ps.eye_tracking_enabled || ps.gaze_score_tracking) prohibited.push('Do NOT look away from the screen for extended periods');
    if (ps.face_detection_enabled || ps.camera_required) prohibited.push('Do NOT cover your face, wear sunglasses, or obscure your identity');

    // Build the "Proctoring Active" list — only features enabled for this level
    const proctorFeatures = [];
    if (ps.fullscreen_required) proctorFeatures.push(['The test runs in fullscreen mode', false]);
    if (ps.max_tab_switches > 0) proctorFeatures.push([`Tab switching is monitored (limit: ${ps.max_tab_switches}${ps.auto_terminate_on_limit ? ', auto-submit on exceeding' : ''})`, false]);
    if (ps.camera_required) proctorFeatures.push(['Your camera will remain on throughout the test', false]);
    if (ps.random_photos_enabled) proctorFeatures.push(['Random photos will be captured and reviewed by AI', false]);
    if (ps.face_detection_enabled) proctorFeatures.push(['Face detection ensures you are present and alone', false]);
    if (ps.identity_verification_required) proctorFeatures.push(['Your face will be verified against your ID before the test', false]);
    if (ps.audio_monitoring_enabled) proctorFeatures.push(['Audio will be monitored for human speech or unusual sounds', false]);
    if (ps.eye_tracking_enabled || ps.gaze_score_tracking) proctorFeatures.push(['Eye/gaze tracking will detect if you look away — a gaze score is calculated', false]);
    if (ps.continuous_video_recording) proctorFeatures.push(['Your entire test session will be video recorded', true]);
    if (ps.screen_sharing_required) proctorFeatures.push(['Screen sharing is required and verified before the test', true]);
    if (ps.audio_check_required) proctorFeatures.push(['Microphone audio level will be tested before the test begins', true]);
    if (ps.mobile_proctoring_enabled) proctorFeatures.push(['A second mobile camera (QR pairing) provides 360° monitoring', true]);

    // Build the "Before You Start" list
    const perms = [];
    if (ps.camera_required) perms.push('camera');
    if (ps.microphone_required) perms.push('microphone');
    if (ps.screen_sharing_required) perms.push('screen sharing');
    const beforeStart = [];
    if (perms.length) beforeStart.push([`Grant ${perms.join(', ')} permission${perms.length > 1 ? 's' : ''} when prompted`, true]);
    if (ps.identity_verification_required) beforeStart.push(['Keep your ID card ready for identity verification', true]);
    if (ps.audio_check_required) beforeStart.push(['Speak a few words into your mic during the audio level check', false]);
    if (ps.camera_required) beforeStart.push(['Ensure good lighting — your face must be clearly visible', false]);

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-blue-900 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8" data-testid="test-instructions">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Test Instructions</h2>
            <p className="text-gray-600 mt-1">{test?.title} &mdash; {questions.length} Questions &mdash; {test?.config?.duration} Minutes</p>
            {isProctored && (
              <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full uppercase tracking-wide" data-testid="proctoring-level-badge">
                Proctoring: {test.proctoring_level.replace('_', ' + ')}
              </span>
            )}
          </div>

          <div className="space-y-3 mb-8">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="font-bold text-red-800 mb-2 text-sm uppercase tracking-wide">Strictly Prohibited</h3>
              <ul className="space-y-1.5 text-sm text-red-700" data-testid="prohibited-list">
                {prohibited.map((item, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="mt-0.5 text-red-500 font-bold">&times;</span> {item}</li>
                ))}
              </ul>
            </div>

            {isProctored && proctorFeatures.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h3 className="font-bold text-blue-800 mb-2 text-sm uppercase tracking-wide">Proctoring Active</h3>
                <ul className="space-y-1.5 text-sm text-blue-700" data-testid="proctoring-feature-list">
                  {proctorFeatures.map(([item, strong], i) => (
                    <li key={i} className="flex items-start gap-2"><span className="mt-0.5">&#8226;</span> {strong ? <strong>{item}</strong> : item}</li>
                  ))}
                </ul>
              </div>
            )}

            {beforeStart.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="font-bold text-amber-800 mb-2 text-sm uppercase tracking-wide">Before You Start</h3>
                <ul className="space-y-1.5 text-sm text-amber-800" data-testid="before-start-list">
                  {beforeStart.map(([item, strong], i) => (
                    <li key={i} className="flex items-start gap-2"><span className="mt-0.5">&#8226;</span> {strong ? <strong>{item}</strong> : item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h3 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wide">General Guidelines</h3>
              <ul className="space-y-1.5 text-sm text-gray-700">
                <li className="flex items-start gap-2"><span className="mt-0.5">&#8226;</span> Ensure stable internet connectivity throughout</li>
                <li className="flex items-start gap-2"><span className="mt-0.5">&#8226;</span> Sit in a well-lit, quiet room with a clean desk</li>
                {isProctored && <li className="flex items-start gap-2"><span className="mt-0.5">&#8226;</span> Keep your face clearly visible to the camera at all times</li>}
                <li className="flex items-start gap-2"><span className="mt-0.5">&#8226;</span> All violations are recorded and reviewed by the examiner</li>
              </ul>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center mb-4">
            By clicking "I Understand, Proceed" you agree to comply with all the above rules.{isProctored ? ' Violations will reduce your trust score and may result in test termination.' : ''}
          </p>

          <button
            onClick={handleProceedFromInstructions}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors text-lg"
            data-testid="proceed-instructions-btn"
          >
            I Understand, Proceed
          </button>
        </div>
      </div>
    );
  }

  // Fullscreen Prompt Phase — requires user click to trigger fullscreen API
  if (phase === PHASE.FULLSCREEN_PROMPT) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-blue-900 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center" data-testid="fullscreen-prompt">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Fullscreen Required</h2>
          <p className="text-gray-600 mb-2">
            This test must be taken in fullscreen mode to ensure exam integrity.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Click the button below to enter fullscreen and start your test. Do not exit fullscreen during the test.
          </p>
          <button
            onClick={handleEnterFullscreenAndStart}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors text-lg"
            data-testid="enter-fullscreen-btn"
          >
            Enter Fullscreen & Start Test
          </button>
          <p className="text-xs text-gray-400 mt-4">
            Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">Esc</kbd> to exit fullscreen (violation will be recorded)
          </p>
        </div>
      </div>
    );
  }

  // If we're not in TAKING_TEST phase yet and not in other phases, show loading
  if (phase !== PHASE.TAKING_TEST && phase !== PHASE.FULLSCREEN_PROMPT && phase !== PHASE.INSTRUCTIONS) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Preparing Test...</h2>
          <p className="text-gray-600">Setting up your test environment</p>
        </div>
      </div>
    );
  }

  // At this point, phase === TAKING_TEST
  // Make sure we have questions loaded
  if (!isReady || !questions || questions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Almost ready...</h2>
          <p className="text-gray-600">Finalizing test setup</p>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;

  // Main test content
  const TestContent = (
    <div className="test-container" data-testid="test-container">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b shadow-sm z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="test-title">{test.title}</h1>
              <p className="text-sm text-gray-600">
                Question {currentQuestion + 1} of {questions.length}
              </p>
            </div>
            <div className="flex items-center gap-6">
              {test.config.proctoring_enabled && (
                <div className="flex items-center text-sm">
                  <AlertTriangle className="w-4 h-4 text-orange-500 mr-1" />
                  <span data-testid="violations-count">Violations: {violations}/{test.config.tab_switch_limit}</span>
                </div>
              )}
              <div className={`flex items-center text-lg font-bold ${timeRemaining < 300 ? 'text-red-600 timer-warning' : 'text-gray-900'}`}>
                <Clock className="w-5 h-5 mr-2" />
                <span data-testid="timer">{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}</span>
              </div>
            </div>
          </div>
          <Progress value={progress} className="mt-3" />
        </div>
      </div>

      {/* Question Area */}
      <div className="max-w-4xl mx-auto px-6 py-8 pb-24">
        <Card className="question-card" data-testid="question-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full font-medium">
                {question.question_type}
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                {question.points} points
              </span>
            </div>
          </div>
          
          <div className="text-xl font-semibold text-gray-900 mb-6" data-testid="question-text">
            <LaTeXRenderer text={question.question_text} />
          </div>

          {question.media_url && (
            <div className="mb-6">
              {question.media_type === 'image' && (
                <img src={question.media_url} alt="Question media" className="max-w-full rounded-lg" />
              )}
              {question.media_type === 'video' && (
                <video controls className="max-w-full rounded-lg">
                  <source src={question.media_url} />
                </video>
              )}
            </div>
          )}

          {/* Answer Options */}
          <div className="space-y-3">
            {['mcq_single', 'mcq_multiple', 'true_false'].includes(question.question_type) && question.choices && (
              question.choices.map((choice, idx) => (
                <div
                  key={idx}
                  onClick={() => handleAnswerChange(question.question_type === 'mcq_multiple' 
                    ? (answers[question.id]?.includes(idx) 
                      ? answers[question.id].filter(i => i !== idx) 
                      : [...(answers[question.id] || []), idx])
                    : idx
                  )}
                  className={`choice-item ${
                    question.question_type === 'mcq_multiple'
                      ? answers[question.id]?.includes(idx) ? 'selected' : ''
                      : answers[question.id] === idx ? 'selected' : ''
                  }`}
                  data-testid={`choice-${idx}`}
                >
                  <div className="flex items-center">
                    <span className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center mr-3 font-medium">
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <div className="flex-1">
                      <LaTeXRenderer text={choice} />
                    </div>
                  </div>
                </div>
              ))
            )}

            {['fill_blank', 'short_answer'].includes(question.question_type) && (
              <textarea
                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                rows={4}
                placeholder="Type your answer here..."
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(e.target.value)}
                data-testid="text-answer-input"
              />
            )}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 mb-16 relative z-10">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
            data-testid="previous-button"
          >
            Previous
          </Button>
          
          <div className="flex gap-2">
            {currentQuestion < questions.length - 1 ? (
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                data-testid="next-button"
              >
                Next Question
              </Button>
            ) : (
              <Button
                className="bg-green-600 hover:bg-green-700 relative z-20"
                onClick={() => setShowSubmitDialog(true)}
                data-testid="submit-test-button"
              >
                Submit Test
              </Button>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Test?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to submit? You have answered {Object.keys(answers).length} out of {questions.length} questions.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} data-testid="confirm-submit-button">
              Submit Test
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Termination Dialog */}
      <AlertDialog open={showTerminationDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <AlertDialogTitle className="text-red-600">Test Terminated</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p className="text-gray-800 font-medium">Your test has been automatically terminated due to malpractice detection.</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800"><strong>Reason:</strong> {terminationReason}</p>
              </div>
              <p className="text-sm text-gray-600">
                Your partial answers have been submitted and will be evaluated. The admin will be notified of this incident.
              </p>
              <p className="text-sm text-gray-600">
                You answered <strong>{Object.keys(answers).length}</strong> out of <strong>{questions.length}</strong> questions before termination.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => navigate('/dashboard')}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              Return to Dashboard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  // Fullscreen re-entry overlay (shown when user exits fullscreen during test)
  const FullscreenOverlay = showFullscreenModal && phase === PHASE.TAKING_TEST ? (
    <div className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center shadow-2xl" data-testid="fullscreen-reentry-modal">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Fullscreen Exited!</h2>
        <p className="text-red-600 font-medium mb-1">This violation has been recorded.</p>
        <p className="text-gray-600 text-sm mb-6">
          You must remain in fullscreen mode during the test. Repeated violations may result in test termination.
        </p>
        <button
          onClick={handleReEnterFullscreen}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          data-testid="reenter-fullscreen-btn"
        >
          Return to Fullscreen
        </button>
      </div>
    </div>
  ) : null;

  // Wrap in proctoring overlay if proctoring is enabled
  if (proctoringSettings && proctoringSession && test?.proctoring_level !== 'none') {
    return (
      <ProctoringOverlay
        sessionId={proctoringSession.session_id}
        settings={proctoringSettings}
        token={getToken()}
        onTerminate={handleProctoringTerminate}
        onViolationUpdate={(count, trustScoreVal) => {
          setViolations(count);
          if (trustScoreVal !== undefined) setTrustScore(trustScoreVal);
        }}
      >
        {FullscreenOverlay}
        {TestContent}
      </ProctoringOverlay>
    );
  }

  return (
    <>
      {FullscreenOverlay}
      {TestContent}
    </>
  );
}

export default TakeTest;